import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useCookies } from 'react-cookie';
import { DEALER_DATA_COOKIE_NAME, ACURA_SITE_NAME, HONDA_SITE_NAME } from '../../utils/constants';
import { resetUIReducerSections, setBodyTypeLabels, setIsModelCardGridActive } from '../../core/reducers/ui';
import { useAppDispatch, useAppSelector } from '../../core/utils';
import { resetVehicleListFilters, selectFilteredVehicles, setBodyTypeList, setSelectedFilterItemsAction, } from '../../core/reducers/vehicleListFilters';
import { useAppName } from '../../utils/sitecoreContext';
import { variant1, variant2 } from './utils/constants';
import ConfigurationProvider from './service/Provider';
import ModelCardGridUI from './ModelCardGridUI';
import getBodyTypeFiltersFromLookupTables from './utils/getBodyTypeFiltersFromLookupTables';
import getVehiclesForStore from './utils/getVehiclesForStore';
import { addVehicles } from '../../core/reducers/vehicles';
import { selectSelectedBodyTypeFiltersForGTM } from '../../core/reducers/vehicleListFilters';
import useFilterCatalogByAvailableProvinces from './hooks/useFilterCatalogByAvailableProvinces';
import useModelsInventoryStatus from './hooks/useModelsInventoryStatus';
import getModelCardsFromFilteredModels from './utils/getModelCardsFromFilteredModels';
import useModelCardsWithPaymentData from './hooks/useModelCardsWithPaymentData';
import getModelCards from './utils/getModelCards';
import getModelCardsByBodyType from './utils/getModelCardsByBodyType';
import gtmInventoryDataLayer from '../../core/gtm/gtmInventoryDataLayer';
import { resetCurrentVehicle } from '../../core/reducers/currentVehicle';
import { resetOfferOptions } from '../../core/reducers/offerOptions';
import { resetOffers } from '../../core/reducers/offers';
import isSSR from '../../utils/isSSR';
import { useModelCardGridLocation } from './utils/utils';
const ModelCardGrid = ({ fields, rendering }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0;
    const appName = useAppName();
    const dispatch = useAppDispatch();
    const { componentLocation } = useModelCardGridLocation();
    let variant = '';
    switch (appName) {
        case HONDA_SITE_NAME:
            variant = variant1;
            break;
        case ACURA_SITE_NAME:
            variant = variant2;
            break;
        default:
    }
    const appNameConfig = useMemo(() => ({ variant }), [variant]);
    const catalog = (_f = (_e = (_d = (_c = (_b = (_a = fields === null || fields === void 0 ? void 0 : fields.data) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.catalog) === null || _c === void 0 ? void 0 : _c.fields) === null || _d === void 0 ? void 0 : _d.models) === null || _e === void 0 ? void 0 : _e[0]) === null || _f === void 0 ? void 0 : _f.items;
    const nextStepUrl = ((_k = (_j = (_h = (_g = fields === null || fields === void 0 ? void 0 : fields.data) === null || _g === void 0 ? void 0 : _g.value) === null || _h === void 0 ? void 0 : _h.nextStepBaseUrl) === null || _j === void 0 ? void 0 : _j.item) === null || _k === void 0 ? void 0 : _k.value) || {};
    const title = (_o = (_m = (_l = fields === null || fields === void 0 ? void 0 : fields.data) === null || _l === void 0 ? void 0 : _l.value) === null || _m === void 0 ? void 0 : _m.title) === null || _o === void 0 ? void 0 : _o.value;
    const selectedFiltersForGtm = useAppSelector(selectSelectedBodyTypeFiltersForGTM);
    const hasIntegratedFilters = ((_r = (_q = (_p = fields === null || fields === void 0 ? void 0 : fields.data) === null || _p === void 0 ? void 0 : _p.value) === null || _q === void 0 ? void 0 : _q.integrateFilter) === null || _r === void 0 ? void 0 : _r.value) === '1';
    const inventoryStatusPosition = (_v = (_u = (_t = (_s = fields === null || fields === void 0 ? void 0 : fields.data) === null || _s === void 0 ? void 0 : _s.value) === null || _t === void 0 ? void 0 : _t.inventoryStatusPosition) === null || _u === void 0 ? void 0 : _u.value) === null || _v === void 0 ? void 0 : _v.toLowerCase();
    const bodyTypesFromLookupTables = getBodyTypeFiltersFromLookupTables((_0 = (_z = (_y = (_x = (_w = fields === null || fields === void 0 ? void 0 : fields.data) === null || _w === void 0 ? void 0 : _w.value) === null || _x === void 0 ? void 0 : _x.catalog) === null || _y === void 0 ? void 0 : _y.fields) === null || _z === void 0 ? void 0 : _z.lookupTables) === null || _0 === void 0 ? void 0 : _0[0]);
    const [cookies] = useCookies([DEALER_DATA_COOKIE_NAME]);
    // filter catalog by each vehicle's available provinces, and return only vehicles available in the user's province
    const modelsByAvailableProvinces = useFilterCatalogByAvailableProvinces(catalog);
    const modelCards = getModelCards(modelsByAvailableProvinces, componentLocation);
    const modelCardsWithBodyTypes = modelCards.filter(card => { var _a, _b; return (_b = (_a = card.filters) === null || _a === void 0 ? void 0 : _a.bodyType) === null || _b === void 0 ? void 0 : _b.length; });
    const modelCardsWithPaymentData = useModelCardsWithPaymentData(modelCardsWithBodyTypes, hasIntegratedFilters);
    const modelCardsByBodyType = getModelCardsByBodyType(modelCardsWithPaymentData);
    const filteredVehicles = useSelector(selectFilteredVehicles);
    const selectedFilterItems = useAppSelector(state => { var _a; return (_a = state === null || state === void 0 ? void 0 : state.vehicleListFilters) === null || _a === void 0 ? void 0 : _a.selectedFilterItems; });
    const filteredModelCards = getModelCardsFromFilteredModels(modelCardsByBodyType, filteredVehicles, selectedFilterItems);
    const vehiclesForStore = getVehiclesForStore(modelsByAvailableProvinces || []);
    const allModelsForFilterOptions = vehiclesForStore.map(vehicle => ({
        modelYear: vehicle.modelYear,
        filters: vehicle.filters || {},
    }));
    const dealerCookie = cookies[DEALER_DATA_COOKIE_NAME];
    const dealerName = dealerCookie === null || dealerCookie === void 0 ? void 0 : dealerCookie.dealerName;
    const bodyTypeKeys = bodyTypesFromLookupTables.map(obj => Object.keys(obj)[0] || '');
    const filteredResultsCount = Object.values(filteredModelCards).reduce((sum, arr) => sum + arr.length, 0);
    useEffect(() => {
        // add vehicle data to state.vehicles, for use by other modules
        if (vehiclesForStore.length) {
            dispatch(addVehicles(vehiclesForStore));
        }
        if (bodyTypeKeys) {
            // add body type keys to state.vehicleListFilters, for use by other modules
            dispatch(setBodyTypeList(bodyTypeKeys));
            // add body type filters and their user-facing labels to ui.state, for use by other modules
            dispatch(setBodyTypeLabels(bodyTypesFromLookupTables));
        }
        dispatch(setSelectedFilterItemsAction([]));
        dispatch(setIsModelCardGridActive(true));
        return () => {
            dispatch(resetVehicleListFilters());
        };
        // all dependencies are available on mount, and don't change, so we can safely...
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (dealerName && selectedFiltersForGtm) {
            gtmInventoryDataLayer({
                dealerName,
                bodyStyle: selectedFiltersForGtm,
            });
        }
    }, [selectedFiltersForGtm, dealerName]);
    // Fetch legacy inventory status for models
    const { data: modelsInventoryStatus } = useModelsInventoryStatus(hasIntegratedFilters); // disable legacy when in Inventory flow;
    const getDefaultTransmissionInventoryStatus = (model) => {
        var _a;
        return (_a = modelsInventoryStatus === null || modelsInventoryStatus === void 0 ? void 0 : modelsInventoryStatus.find(transmission => (transmission === null || transmission === void 0 ? void 0 : transmission.transmissionKey) === model.defaultTransmission)) === null || _a === void 0 ? void 0 : _a.inventoryStatus;
    };
    useEffect(() => {
        if (!isSSR() && window.location.search)
            return;
        dispatch(resetCurrentVehicle());
        dispatch(resetOffers());
        dispatch(resetOfferOptions());
        dispatch(resetUIReducerSections(['ConfigureBap', 'TrimCardGrid', 'SummaryBar', 'SummaryPage', 'TrimOverview']));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (!filteredModelCards || Object.keys(filteredModelCards).length === 0)
        return null;
    return (React.createElement(ConfigurationProvider, { appNameConfig: appNameConfig },
        React.createElement(ModelCardGridUI, { componentName: rendering === null || rendering === void 0 ? void 0 : rendering.componentName, modelCards: filteredModelCards, bodyTypes: bodyTypesFromLookupTables, nextStepUrl: nextStepUrl, title: title, hasIntegratedFilters: hasIntegratedFilters, inventoryStatusPosition: inventoryStatusPosition, getDefaultTransmissionInventoryStatus: getDefaultTransmissionInventoryStatus, shouldRenderFilters: hasIntegratedFilters, allModelsForFilterOptions: allModelsForFilterOptions, filteredResultsCount: filteredResultsCount })));
};
export default ModelCardGrid;
